import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, getApiError, toCamelCase, toSnakeCase } from '../lib/api'
import { clearQueryCache } from '../lib/queryClient'

export type UserRole = 'client' | 'admin'

export interface User {
  id: number
  email: string
  name: string
  role: UserRole
  createdAt: string
  updatedAt: string
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  name: string
  password: string
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  error: string | null
  isAuthenticated: boolean

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,
      isAuthenticated: false,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/auth/login', credentials)
          const data = toCamelCase(response.data as Record<string, unknown>) as unknown as {
            user: User
            token: string
          }

          localStorage.setItem('gestorplus_token', data.token)

          set({
            user: data.user,
            token: data.token,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error) {
          const apiError = getApiError(error)
          set({ error: apiError.message, isLoading: false })
          throw error
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post(
            '/auth/register',
            toSnakeCase(data as unknown as Record<string, unknown>)
          )
          const result = toCamelCase(response.data as Record<string, unknown>) as unknown as {
            user: User
            token: string
          }

          localStorage.setItem('gestorplus_token', result.token)

          set({
            user: result.user,
            token: result.token,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error) {
          const apiError = getApiError(error)
          set({ error: apiError.message, isLoading: false })
          throw error
        }
      },

      logout: async () => {
        set({ isLoading: true })
        try {
          const { token } = get()
          if (token) {
            await api.post('/auth/logout')
          }
        } catch {
          // Ignore errors on logout
        } finally {
          localStorage.removeItem('gestorplus_token')
          // Clear all React Query cache to prevent data leakage between users
          clearQueryCache()
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        }
      },

      checkAuth: async () => {
        const { token } = get()
        if (!token) {
          set({ isAuthenticated: false, user: null })
          return
        }

        set({ isLoading: true })
        try {
          const response = await api.get('/auth/me')
          const user = toCamelCase(response.data as Record<string, unknown>) as unknown as User
          set({ user, isAuthenticated: true, isLoading: false })
        } catch {
          localStorage.removeItem('gestorplus_token')
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'gestorplus-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
